import { useState } from 'react';
import { Contact } from '@/types/api';
import { contactsApi } from '@/api/contacts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

const AVAILABLE_AVATARS = [
  'm1.png',
  'm2.png',
  'm3.png',
  'f1.png',
  'f2.png',
  'f3.png',
];

interface AvatarSelectorProps {
  contact: Contact;
  onAvatarUpdate?: (avatar: string) => void;
}

export function AvatarSelector({ contact, onAvatarUpdate }: AvatarSelectorProps) {
  const [selectedAvatar, setSelectedAvatar] = useState(contact.avatar || '');
  const queryClient = useQueryClient();

  const updateAvatarMutation = useMutation({
    mutationFn: (avatar: string) =>
      contactsApi.partialUpdate(contact.id, { avatar }),
    onSuccess: (updatedContact) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
      setSelectedAvatar(updatedContact.avatar || '');
      toast.success('Avatar updated successfully');
      onAvatarUpdate?.(updatedContact.avatar || '');
    },
    onError: () => {
      toast.error('Failed to update avatar');
    },
  });

  const handleAvatarClick = (avatar: string) => {
    if (avatar !== selectedAvatar) {
      updateAvatarMutation.mutate(avatar);
    }
  };

  return (
    <div className="w-full">
      <p className="text-center text-sm text-gray-600 mb-3">Click to select an avatar</p>
      <div className="flex gap-4 items-center justify-center flex-wrap">
        {AVAILABLE_AVATARS.map((avatar) => (
          <button
            key={avatar}
            onClick={() => handleAvatarClick(avatar)}
            disabled={updateAvatarMutation.isPending}
            className={`relative rounded-full transition-all ${
              selectedAvatar === avatar
                ? 'ring-2 ring-wabisabi-plum ring-offset-1'
                : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
            }`}
          >
            <Avatar className="w-12 h-12">
              <AvatarImage
                src={`/images/avatars/${avatar}`}
                alt={avatar}
              />
              <AvatarFallback>{avatar[0].toUpperCase()}</AvatarFallback>
            </Avatar>
          </button>
        ))}
      </div>
    </div>
  );
}
